<template>
  <ui-select
    :value="value"
    class="status-picker"
    @change="statusChanged"
    :items="items"
    item-text="text"
    item-value="value"
    :prepend-inner-icon="icon"
    :autocomplete="false"
    :hide-details="1"
  />
</template>

<script setup lang="ts">
import { useStore } from '@/store';
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import query from '@/components/query/query-constructor';
import { useI18n } from '@/composables/useI18n';
import { useUrl } from '@/composables/useUrl';
import { useTableQuery } from '@/composables/useTableQuery';
import { usePickerPushUrl } from '@/composables/usePickerPushUrl';
import { $emitter } from '@/services/emitter';

const store = useStore();

const { t } = useI18n();
const route = useRoute();
const { isDashboard } = useUrl();
const tableQueryApi = useTableQuery();
const { pushReportUrl } = usePickerPushUrl();

const isPostback = computed(() => route.name === 'app.postbacks');
const tableQueryParam = computed(() => (isPostback.value ? 'postbackStatus' : 'status'));
const defaultValue = computed(() => (isPostback.value ? 'all' : 'active'));

const value = computed(() => {
  if (isDashboard.value) {
    return store.dashboardQueryTemporary.status || defaultValue.value;
  }
  return tableQueryApi.tableQueryForCurrentTab.value[tableQueryParam.value] || defaultValue.value;
});

const items = computed(() => {
  if (isPostback.value) {
    return [
      { value: 'all', text: t('All'), icon: 'fas fa-list' },
      { value: '1', text: t('sent'), icon: 'fas fa-check' },
      { value: '0', text: t('failed'), icon: 'fas fa-xmark' },
    ];
  }

  return [
    { value: 'active', text: t('____x.active'), icon: 'fas fa-play' },
    { value: 'archived', text: t('Archived'), icon: 'fas fa-archive' },
    { value: 'withTraffic', text: t('With Traffic'), icon: 'fas fa-heartbeat' },
    { value: 'all', text: t('All'), icon: 'fas fa-list' },
  ];
});

const icon = computed(() => items.value.find(item => item.value === value.value)?.icon);

const applyDataFromUrl = () => {
  if (isDashboard.value) {
    const { status } = query.decode(route.params.data as string) || {};
    const statusValue = status || defaultValue.value;
    store.patchDashboardQueryTemporaryField({ name: 'status', value: statusValue });
    store.patchDashboardQueryField({ name: 'status', value: statusValue });
    return;
  }

  const params = query.decode(route.params.data as string) || {};
  tableQueryApi.patchTableQueryField({
    name: tableQueryParam.value,
    value: params[tableQueryParam.value] || defaultValue.value,
  });
};

const statusChanged = (selectedValue: string) => {
  if (isDashboard.value) {
    store.patchDashboardQueryTemporaryField({ name: 'status', value: selectedValue });
    return;
  }

  tableQueryApi.patchTableQueryField({ name: tableQueryParam.value, value: selectedValue });
  pushReportUrl();
  $emitter.emit('reset-offset');
};

onMounted(() => {
  applyDataFromUrl();
});
</script>

<style lang="scss">
  .status-picker {
    align-self: center;
  }
</style>
